<script setup lang="ts">
  // Ported from cartadigital-pos app/components/pos/table/PosTableNav.vue.
  // Data comes live from the socket-fed ordersStore instead of a read model.
  import { computed, nextTick, onMounted, ref, watch } from 'vue';
  import { breakpointsTailwind, useBreakpoints } from '@vueuse/core';
  import { OrderStatus } from '@/enums/OrderEnums';
  import { usePosMoney } from '@/composition/use-pos-money';
  import { usePosReservationTables } from '@/composition/reservations/use-pos-reservation-tables';
  import { isPosTableOccupied } from '@/domain/reservations/pos-table-hints';
  import PosUiBadge from '@/components/pos-v2/ui/PosUiBadge.vue';
  import PosUiScrollable from '@/components/pos-v2/ui/PosUiScrollable.vue';

  interface ITableNavItem {
    id: number;
    name: string;
    ordersCount: number;
    total: number;
    status: 'free' | 'occupied' | 'attention';
  }

  const props = defineProps<{
    currentTableId: number;
  }>();

  const emit = defineEmits<{
    (e: 'select', tableId: number): void;
  }>();

  const { tables: tableViews } = usePosReservationTables();
  const money = usePosMoney();

  const breakpoints = useBreakpoints(breakpointsTailwind);
  const isDesktop = breakpoints.greaterOrEqual('md');

  const tables = computed<ITableNavItem[]>(() => {
    const items: ITableNavItem[] = [];
    for (const table of tableViews.value ?? []) {
      const orders = table.orders;

      let status: ITableNavItem['status'] = 'free';
      if (isPosTableOccupied(table)) {
        status = orders.some((o) => o.status === OrderStatus.Received)
          ? 'attention'
          : 'occupied';
      }

      items.push({
        id: table.id,
        name: table.name,
        ordersCount: orders.length,
        total: orders.reduce((sum, o) => sum + o.total, 0),
        status,
      });
    }

    return items;
  });

  // Keep the active table visible in the rail (matters on the horizontal
  // mobile strip, where the current table can start off-screen).
  const rootEl = ref<HTMLElement>();

  async function scrollActiveIntoView() {
    await nextTick();
    const active = rootEl.value?.querySelector('[data-active="true"]');
    active?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    });
  }

  onMounted(scrollActiveIntoView);
  watch(() => props.currentTableId, scrollActiveIntoView);
  // Cold navigation: tables can arrive after mount, so scroll once the rail
  // actually has items.
  watch(
    () => tables.value.length,
    (len, prev) => {
      if (len > 0 && prev === 0) scrollActiveIntoView();
    }
  );
</script>

<template>
  <div
    ref="rootEl"
    class="pos-v2-table-nav"
    :class="{ desktop: isDesktop, mobile: !isDesktop }"
  >
    <div v-if="isDesktop" class="rail-title">
      {{ $t('posV2.tablesRailTitle') }}
    </div>

    <PosUiScrollable
      class="nav-container"
      :orientation="isDesktop ? 'vertical' : 'horizontal'"
      variant="overlay"
      theme="page"
      :offset-top="10"
      :offset-bottom="10"
      :offset-left="10"
      :offset-right="10"
      size="sm"
      :arrows="true"
      scroll-class="nav-scroll"
    >
      <button
        v-for="table in tables"
        :key="table.id"
        type="button"
        class="table-nav-item"
        :class="{
          active: table.id === props.currentTableId,
          'status-free': table.status === 'free',
          'status-occupied': table.status === 'occupied',
          'status-attention': table.status === 'attention',
        }"
        :data-active="table.id === props.currentTableId ? 'true' : 'false'"
        @click="emit('select', table.id)"
      >
        <div class="item-header">
          <span class="status-dot" aria-hidden="true"></span>
          <span class="table-name">{{ table.name }}</span>
          <div class="item-badges">
            <span v-if="isDesktop && table.total > 0" class="meta-text">
              {{ money.formatComputedMoney(table.total) }}
            </span>
            <PosUiBadge
              v-if="table.ordersCount > 0"
              :label="table.ordersCount"
              size="xs"
              color="neutral"
              variant="soft"
            />
          </div>
        </div>
      </button>
    </PosUiScrollable>
  </div>
</template>

<style scoped lang="scss">
  @keyframes attention-dot-pulse {
    0%,
    100% {
      background-color: rgb(248 113 113);
    }
    50% {
      background-color: rgb(220 38 38);
    }
  }

  // Quiet nav rail: flat rows on neutral-50, status as a leading dot, active
  // row = white + orange left indicator.
  .pos-v2-table-nav {
    @apply tw-relative tw-flex-none tw-border-neutral-200 tw-bg-neutral-100;

    .rail-title {
      @apply tw-flex-none tw-px-4 tw-pb-1 tw-pt-3 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-neutral-400;
    }

    &.mobile {
      @apply tw-flex tw-border-b;

      :deep(.nav-scroll) {
        @apply tw-flex tw-gap-1.5 tw-overflow-x-auto tw-p-2;
        scrollbar-width: none;
        -ms-overflow-style: none;

        &::-webkit-scrollbar {
          @apply tw-hidden;
        }
      }

      .table-nav-item {
        @apply tw-min-w-[110px] tw-flex-shrink-0 tw-border tw-border-neutral-200;
      }
    }

    &.desktop {
      @apply tw-flex tw-h-full tw-w-56 tw-flex-col tw-border-r;

      :deep(.nav-container) {
        @apply tw-min-h-0 tw-flex-1;
      }

      :deep(.nav-scroll) {
        @apply tw-flex tw-h-full tw-flex-col tw-gap-0.5 tw-overflow-y-auto tw-p-2;
      }

      .table-nav-item {
        @apply tw-w-full;
      }
    }

    .table-nav-item {
      @apply tw-relative tw-flex tw-min-h-[48px] tw-cursor-pointer tw-select-none tw-items-center tw-rounded-lg tw-bg-transparent tw-px-3 tw-py-2 tw-transition-colors tw-duration-150;

      &::before {
        // Active indicator bar (kept in the layout so rows don't shift).
        content: '';
        @apply tw-absolute tw-bottom-2 tw-left-0 tw-top-2 tw-w-[3px] tw-rounded-full tw-bg-transparent;
      }

      &.active {
        @apply tw-bg-white tw-shadow-sm;

        &::before {
          @apply tw-bg-orange-600;
        }

        .table-name {
          @apply tw-font-semibold;
        }
      }

      &:not(.active):hover {
        // One step darker than the neutral-100 rail so hover stays visible.
        @apply tw-bg-neutral-200;
      }

      .item-header {
        @apply tw-flex tw-w-full tw-items-center tw-gap-2;

        .status-dot {
          @apply tw-h-2 tw-w-2 tw-flex-none tw-rounded-full tw-bg-neutral-300;
        }

        .table-name {
          @apply tw-min-w-0 tw-flex-1 tw-truncate tw-text-left tw-text-sm tw-font-medium tw-text-neutral-900;
        }

        .item-badges {
          @apply tw-flex tw-flex-none tw-items-center tw-gap-1.5;

          .meta-text {
            @apply tw-text-xs tw-font-medium tw-tabular-nums tw-text-neutral-500;
          }
        }
      }

      &.status-occupied .status-dot {
        @apply tw-bg-amber-400;
      }

      &.status-attention .status-dot {
        @apply tw-bg-red-500;
        animation: attention-dot-pulse 2s ease-in-out infinite;
      }
    }
  }
</style>
